import { useMutation } from '@apollo/client';
import React from 'react';

import {
  eluveAdminHasuraContext,
  useCompleteFragment,
} from '@eluve/apollo-client';
import { Switch, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import {
  BundleBlockerTooltip,
  type TenantBundleBlocker,
} from './BundleBlockerTooltip';

export const tenantPromptTemplateFragment = graphql(`
  fragment TenantPromptTemplate on TenantPromptTemplates @_unmask {
    __typename
    tenantId
    promptTemplateId
    isEnabled
    updatedAt
  }
`);

const setTenantPromptTemplateMutation = graphql(
  `
    mutation setTenantPromptTemplate(
      $tenantId: uuid!
      $promptTemplateId: uuid!
      $isEnabled: Boolean!
    ) {
      insertTenantPromptTemplatesOne(
        object: {
          tenantId: $tenantId
          promptTemplateId: $promptTemplateId
          isEnabled: $isEnabled
        }
        onConflict: {
          constraint: tenant_prompt_templates_pkey
          updateColumns: [isEnabled]
        }
      ) {
        ...TenantPromptTemplate
      }
    }
  `,
  [tenantPromptTemplateFragment],
);

export interface TenantPromptTemplateToggleProps {
  tenantId: string;
  name: string;
  promptTemplateId: string;
  blockedBy?: TenantBundleBlocker[];
}

export const TenantPromptTemplateToggle: React.FC<
  TenantPromptTemplateToggleProps
> = ({ tenantId, name, promptTemplateId, blockedBy }) => {
  const toast = useToast();
  const [updateTenantPromptTemplate] = useMutation(
    setTenantPromptTemplateMutation,
    {
      onError: () =>
        toast.error(`Failed to update access to prompt template ${name}`),
      optimisticResponse: (data) => ({
        insertTenantPromptTemplatesOne: {
          __typename: 'TenantPromptTemplates' as const,
          updatedAt: new Date().toISOString(),
          ...data,
        },
      }),
    },
  );

  const submit = async (isEnabled: boolean) => {
    await updateTenantPromptTemplate({
      context: eluveAdminHasuraContext,
      variables: {
        tenantId,
        promptTemplateId,
        isEnabled,
      },
    });
  };

  const data = useCompleteFragment({
    fragment: tenantPromptTemplateFragment,
    key: {
      promptTemplateId,
      tenantId,
    },
    strict: false,
  });

  const isBlocked = blockedBy !== undefined && blockedBy.length > 0;

  const switchEl = (
    <Switch
      size="sm"
      disabled={isBlocked}
      onCheckedChange={(isChecked) => submit(isChecked)}
      checked={data?.isEnabled ?? false}
    />
  );

  if (!isBlocked) {
    return switchEl;
  }

  return (
    <BundleBlockerTooltip blockedBy={blockedBy}>
      {switchEl}
    </BundleBlockerTooltip>
  );
};
